/**
 * A rendered email signature, on paper — Settings › Customers › Email
 * signatures and under the composer (Aaron, Oct 24, note 2.7).
 *
 * **In a shadow root, not a frame.** A signature's inline styles are written
 * for a mail client; drawn straight into the app they would meet its reset
 * stylesheet (`img { display: block }`, unmargined paragraphs) and be wrong,
 * and a stray `width: 600px` could push the page about. A shadow root is the
 * isolation that needs: the app's rules do not reach in and the signature's
 * do not reach out. It is not a sandboxed `<iframe srcdoc>` on purpose —
 * there is nothing in it to sandbox (below), and a frame without
 * `allow-scripts` makes Chromium log *Blocked script execution in
 * 'about:srcdoc'* whenever anything injects a script into every frame, as
 * Playwright's trace recorder does on every board run.
 *
 * **What goes in is ours.** Only `renderSignature`'s output is drawn here:
 * the allow-list of `shared/src/email-signature.ts`, run on save and again
 * over every render — no script, no `on…` handler, no frame, no URL but
 * `http(s)`, `mailto` and `tel`, no style that loads anything, and images
 * only from Rowted's own `/api/v1/email-images/`. The note editor and the
 * proposal's terms put their sanitised HTML in the page on the same footing.
 * Every link opens beside the app, never in place of it.
 *
 * The paper is the system's light canvas (`color-scheme: light`, `Canvas`,
 * `CanvasText`), because an email is a white page in every mail client, dark
 * app or not — and a system colour is not a hex this file would have to own.
 */
import { useLayoutEffect, useRef } from 'react'
import { cn } from '../lib/cn.js'

/** The paper's own rules, inside the shadow root: a mail client's defaults, not the app's. */
const PAPER = ':host{display:block;color-scheme:light;background:Canvas;color:CanvasText;'
  + 'font-family:Arial,Helvetica,sans-serif;font-size:13px;line-height:1.45;font-weight:400;'
  + 'letter-spacing:normal;overflow-wrap:anywhere}'
  + '.paper{overflow:hidden}img{max-width:100%;height:auto}a{color:LinkText}p{margin:0}'

export function SignaturePaper({ html, title, className, maxHeight }: {
  /** Rendered and sanitised (`renderSignature`). */
  html: string
  /** What a screen reader calls it. */
  title: string
  className?: string
  /** Past this it scrolls — under a composer there is not room for a letterhead. */
  maxHeight?: number
}) {
  const host = useRef<HTMLDivElement>(null)
  useLayoutEffect(() => {
    const element = host.current
    if (!element) return
    const root = element.shadowRoot ?? element.attachShadow({ mode: 'open' })
    root.innerHTML = `<style>${PAPER}</style><div class="paper" part="paper">${html}</div>`
    for (const link of root.querySelectorAll('a[href]')) {
      link.setAttribute('target', '_blank')
      link.setAttribute('rel', 'noopener noreferrer')
    }
  }, [html])
  return (
    <div
      ref={host}
      role="group"
      aria-label={title}
      data-signature-paper
      className={cn('block w-full', maxHeight !== undefined && 'overflow-y-auto', className)}
      style={maxHeight !== undefined ? { maxHeight } : undefined}
    />
  )
}
